import { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerLayout from '../components/ManagerLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, MapPin, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerLocations = ({ manager, onLogout }) => {
  const [locations, setLocations] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Error fetching locations');
    }
  };

  const handleAddLocation = async () => {
    if (!formData.name) {
      toast.error('Please enter a location name');
      return;
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.post(`${API}/locations`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Location added successfully');
      setIsAddDialogOpen(false);
      setFormData({ name: '', address: '' });
      fetchLocations();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error adding location'
      );
    }
  };

  const handleEditLocation = async () => {
    if (!formData.name) {
      toast.error('Please enter a location name');
      return;
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.put(
        `${API}/locations/${selectedLocation.id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Location updated successfully');
      setIsEditDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error updating location'
      );
    }
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (location) => {
    setSelectedLocation(location);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteLocation = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.delete(`${API}/locations/${selectedLocation.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Location deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error deleting location'
      );
    }
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Locations</h1>
          <p className="text-gray-600">Manage your store locations</p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900 text-white shadow-lg"
              data-testid="add-location-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-location-dialog">
            <DialogHeader>
              <DialogTitle>Add New Location</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Name
                </label>
                <Input
                  placeholder="Downtown Store"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  data-testid="location-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address (Optional)
                </label>
                <Input
                  placeholder="123 Main St, City, State"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  data-testid="location-address-input"
                />
              </div>
              <Button
                onClick={handleAddLocation}
                className="w-full"
                data-testid="submit-add-location"
              >
                Add Location
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => (
          <Card
            key={location.id}
            className="p-6 hover:shadow-lg transition-all"
            data-testid={`location-card-${location.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-900 to-black rounded-lg flex items-center justify-center shadow-lg">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {location.name}
                  </h3>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(location)}
                  data-testid={`edit-location-${location.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {manager?.role === 'Administrator' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(location)}
                    data-testid={`delete-location-${location.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {location.address && (
              <p className="text-sm text-gray-600">{location.address}</p>
            )}
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent data-testid="edit-location-dialog">
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Name
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                data-testid="edit-location-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address (Optional)
              </label>
              <Input
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                data-testid="edit-location-address-input"
              />
            </div>
            <Button
              onClick={handleEditLocation}
              className="w-full"
              data-testid="submit-edit-location"
            >
              Update Location
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent data-testid="delete-location-dialog">
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-700">
              Are you sure you want to delete <strong>{selectedLocation?.name}</strong>?
            </p>
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded">
              ⚠️ This action cannot be undone. This location will be permanently removed.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                data-testid="cancel-delete-location"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteLocation}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-location"
              >
                Delete Location
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ManagerLayout>
  );
};

export default ManagerLocations;